import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Switch, 
  TimePicker, 
  Button, 
  Space, 
  Typography, 
  Divider,
  message,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { UPDATE_CAMPAIGN_HOURS, GET_ACCOUNT } from '../mutations/account';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const daysOfWeek = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

const CampaignHoursSettings = ({ account }) => {
  const [campaignHours, setCampaignHours] = useState({});
  const [updateCampaignHours, { loading }] = useMutation(UPDATE_CAMPAIGN_HOURS, {
    refetchQueries: [{ query: GET_ACCOUNT }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    console.log('CampaignHoursSettings: account.campaignHours recebido:', account?.campaignHours);
    
    if (account?.campaignHours) {
      // Normalizar dados recebidos do backend para garantir estrutura correta
      const normalizedHours = {};
      daysOfWeek.forEach(day => {
        const dayData = account.campaignHours[day.key];
        if (dayData) {
          normalizedHours[day.key] = {
            'always_open': dayData.always_open || dayData['always_open'] || false,
            'intervals': dayData.intervals || dayData['intervals'] || []
          };
        } else {
          // Valor padrão se o dia não existe
          normalizedHours[day.key] = {
            'always_open': false,
            'intervals': ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day.key) 
              ? [{ start: '09:00', end: '18:00' }] 
              : []
          };
        }
      });
      console.log('CampaignHoursSettings: dados normalizados:', normalizedHours);
      setCampaignHours(normalizedHours);
    } else {
      // Valores padrão se não existir configuração
      const defaultHours = {};
      daysOfWeek.forEach(day => {
        if (['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day.key)) {
          defaultHours[day.key] = {
            'always_open': false,
            'intervals': [{ start: '09:00', end: '18:00' }]
          };
        } else {
          defaultHours[day.key] = {
            'always_open': false,
            'intervals': []
          };
        }
      });
      console.log('CampaignHoursSettings: valores padrão:', defaultHours);
      setCampaignHours(defaultHours);
    }
  }, [account]);

  const handleAlwaysOpenChange = (dayKey, checked) => {
    console.log(`CampaignHoursSettings: Alterando ${dayKey} sempre aberto para:`, checked);
    setCampaignHours(prev => {
      const newState = {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          always_open: checked,
          intervals: checked ? [] : prev[dayKey]?.intervals || []
        }
      };
      console.log('CampaignHoursSettings: Novo estado:', newState);
      return newState;
    });
  };

  const handleIntervalChange = (dayKey, intervalIndex, field, value) => {
    const timeString = value ? value.format('HH:mm') : '';
    setCampaignHours(prev => {
      const newIntervals = [...(prev[dayKey]?.intervals || [])];
      newIntervals[intervalIndex] = {
        ...newIntervals[intervalIndex],
        [field]: timeString
      };
      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          'intervals': newIntervals
        }
      };
    });
  };

  const addInterval = (dayKey) => {
    setCampaignHours(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        'intervals': [
          ...(prev[dayKey]?.intervals || []),
          { start: '09:00', end: '18:00' }
        ]
      }
    }));
  };

  const removeInterval = (dayKey, intervalIndex) => {
    setCampaignHours(prev => {
      const newIntervals = [...(prev[dayKey]?.intervals || [])];
      newIntervals.splice(intervalIndex, 1);
      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          'intervals': newIntervals
        }
      };
    });
  };

  const validateIntervals = () => {
    const errors = [];
    
    daysOfWeek.forEach(day => {
      const dayConfig = campaignHours[day.key];
      if (!dayConfig?.always_open && dayConfig?.intervals?.length > 1) {
        const intervals = dayConfig.intervals;
        
        // Verificar sobreposições e redundâncias
        for (let i = 0; i < intervals.length; i++) {
          for (let j = i + 1; j < intervals.length; j++) {
            const interval1 = intervals[i];
            const interval2 = intervals[j];
            
            if (!interval1.start || !interval1.end || !interval2.start || !interval2.end) continue;
            
            const start1 = interval1.start;
            const end1 = interval1.end;
            const start2 = interval2.start;
            const end2 = interval2.end;
            
            // Verificar se há sobreposição
            if ((start1 <= start2 && end1 >= start2) || (start2 <= start1 && end2 >= start1)) {
              // Verificar se um está completamente dentro do outro
              if ((start1 <= start2 && end1 >= end2)) {
                errors.push(`${day.label}: Intervalo ${start2}-${end2} está completamente dentro de ${start1}-${end1} e é redundante`);
              } else if ((start2 <= start1 && end2 >= end1)) {
                errors.push(`${day.label}: Intervalo ${start1}-${end1} está completamente dentro de ${start2}-${end2} e é redundante`);
              } else {
                errors.push(`${day.label}: Intervalos ${start1}-${end1} e ${start2}-${end2} se sobrepõem`);
              }
            }
          }
        }
      }
    });
    
    return errors;
  };

  const handleSave = async () => {
    console.log('CampaignHoursSettings: Iniciando handleSave');
    console.log('CampaignHoursSettings: campaignHours atual:', campaignHours);
    
    try {
      // Validar intervalos antes de salvar
      const validationErrors = validateIntervals();
      if (validationErrors.length > 0) {
        message.error({
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>Não é possível salvar. Problemas encontrados nos horários:</div>
              {validationErrors.map((error, index) => (
                <div key={index} style={{ fontSize: '12px' }}>• {error}</div>
              ))}
            </div>
          ),
          duration: 8
        });
        
        // Impedir o salvamento
        return;
      }
    
      // Garantir que todos os dias estão presentes e na estrutura correta
      const formattedHours = {};
      daysOfWeek.forEach(day => {
        const dayConfig = campaignHours[day.key] || { 'always_open': false, 'intervals': [] };
        console.log(`CampaignHoursSettings: Processando ${day.key}:`, dayConfig);
        formattedHours[day.key] = {
          'always_open': dayConfig['always_open'] || false,
          'intervals': dayConfig['intervals'] || []
        };
      });
      
      console.log('CampaignHoursSettings: Estado antes do formato:', campaignHours);
      console.log('CampaignHoursSettings: Salvando horários formatados:', formattedHours);
      console.log('CampaignHoursSettings: Estrutura detalhada:');
      Object.keys(formattedHours).forEach(day => {
        console.log(`  ${day}:`, {
          type: typeof formattedHours[day],
          hasAlwaysOpen: formattedHours[day].hasOwnProperty('always_open'),
          hasIntervals: formattedHours[day].hasOwnProperty('intervals'),
          value: formattedHours[day]
        });
      });
      
      const result = await updateCampaignHours({
        variables: { campaignHours: formattedHours }
      });
      
      console.log('CampaignHoursSettings: Resultado da mutation:', result);
      
      console.log('CampaignHoursSettings: Resultado completo da mutation:', JSON.stringify(result, null, 2));
      
      if (result.data?.updateCampaignHours?.errors?.length > 0) {
        const errors = result.data.updateCampaignHours.errors;
        console.error('CampaignHoursSettings: Erros retornados:', errors);
        console.error('CampaignHoursSettings: Tipo dos erros:', typeof errors);
        console.error('CampaignHoursSettings: Array length:', errors.length);
        console.error('CampaignHoursSettings: Cada erro:', errors.map((err, idx) => `${idx}: ${err}`));
        message.error(`Erro ao salvar: ${errors.join(', ')}`);
        return;
      }
      
      message.success('Horários das campanhas atualizados com sucesso!');
    } catch (error) {
      console.error('CampaignHoursSettings: Erro na mutation:', error);
      message.error('Erro ao salvar horários das campanhas: ' + error.message);
    }
  };

  const renderDayConfiguration = (day) => {
    const dayConfig = campaignHours[day.key] || { 'always_open': false, 'intervals': [] };
    
    return (
      <Card 
        key={day.key} 
        size="small" 
        title={
          <Space>
            <ClockCircleOutlined />
            {day.label}
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16} align="middle">
          <Col span={12}>
            <Space>
              <Switch
                checked={dayConfig['always_open']}
                onChange={(checked) => handleAlwaysOpenChange(day.key, checked)}
              />
              <Text>Sempre Aberto</Text>
              <Tooltip title="Quando ativado, as campanhas podem ser enviadas a qualquer horário neste dia">
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </Space>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            {!dayConfig['always_open'] && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => addInterval(day.key)}
              >
                Adicionar Horário
              </Button>
            )}
          </Col>
        </Row>
        
        {!dayConfig['always_open'] && dayConfig['intervals']?.map((interval, index) => (
          <Row key={index} gutter={8} style={{ marginTop: 8 }} align="middle">
            <Col span={8}>
              <TimePicker
                value={interval.start ? dayjs(interval.start, 'HH:mm') : null}
                format="HH:mm"
                placeholder="Início"
                onChange={(value) => handleIntervalChange(day.key, index, 'start', value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Text>até</Text>
            </Col>
            <Col span={8}>
              <TimePicker
                value={interval.end ? dayjs(interval.end, 'HH:mm') : null}
                format="HH:mm"
                placeholder="Fim"
                onChange={(value) => handleIntervalChange(day.key, index, 'end', value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={6} style={{ textAlign: 'right' }}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => removeInterval(day.key, index)}
              />
            </Col>
          </Row>
        ))}
        
        {!dayConfig['always_open'] && (!dayConfig['intervals'] || dayConfig['intervals'].length === 0) && (
          <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>
            <Text type="secondary">Nenhum horário configurado - campanhas não serão enviadas neste dia</Text>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <Title level={4}>
        <ClockCircleOutlined /> Horários das Campanhas
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Configure os horários em que as campanhas podem ser enviadas. Essas configurações se aplicam a todas as campanhas criadas.
      </Text>
      
      <Row gutter={16}>
        <Col span={12}>
          {daysOfWeek.slice(0, 4).map(day => renderDayConfiguration(day))}
        </Col>
        <Col span={12}>
          {daysOfWeek.slice(4).map(day => renderDayConfiguration(day))}
        </Col>
      </Row>
      
      <Divider />
      
      <Row justify="end">
        <Col>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleSave}
          >
            Salvar Configurações
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default CampaignHoursSettings; 